import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

import useDiscountTierService from "../../../api/payments/discountTiers/discountTier.service";
import { DiscountTier } from "../../../api/payments/discountTiers/discountTier.responses";
import { Button } from "../../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/Table";
import { ActiveStatusBadge } from "../shared/ActiveStatusBadge";
import { DeactivateDialog } from "../shared/DeactivateDialog";
import { TabHeader } from "../shared/TabHeader";
import DiscountTierForm from "./DiscountTierForm";

const createSchema = z.object({
  code: z
    .string()
    .min(1, "Required")
    .max(20)
    .regex(/^[A-Za-z0-9_]+$/, "Use letters, numbers and underscores only"),
  name: z.string().min(1, "Required"),
  percentageOff: z.coerce
    .number()
    .int()
    .min(1, "Must be at least 1")
    .max(100, "Max 100"),
});

const editSchema = z.object({
  name: z.string().min(1, "Required"),
  percentageOff: z.coerce.number().int().min(1).max(100),
  isActive: z.boolean(),
});

type CreateFormValues = z.infer<typeof createSchema>;
type EditFormValues = z.infer<typeof editSchema>;

function DiscountTiersTab() {
  const service = useDiscountTierService();
  const [tiers, setTiers] = useState<DiscountTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTier, setEditTier] = useState<DiscountTier | null>(null);
  const [deleteCode, setDeleteCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const createForm = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { code: "", name: "", percentageOff: 10 },
  });

  const editForm = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: "", percentageOff: 10, isActive: true },
  });

  const fetchTiers = () => {
    service
      .listDiscountTiers()
      .then((res) => setTiers(res.data))
      .catch(() => toast.error("Failed to load discount tiers"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTiers();
  }, []);

  const handleCreate = async (values: CreateFormValues) => {
    setSubmitting(true);
    try {
      await service.createDiscountTier(values);
      toast.success("Discount tier created");
      setCreateOpen(false);
      createForm.reset();
      fetchTiers();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to create discount tier"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOpen = (tier: DiscountTier) => {
    setEditTier(tier);
    editForm.reset({
      name: tier.name,
      percentageOff: tier.percentageOff,
      isActive: tier.isActive,
    });
  };

  const handleEdit = async (values: EditFormValues) => {
    if (!editTier) return;
    setSubmitting(true);
    try {
      await service.updateDiscountTier(editTier.code, values);
      toast.success("Discount tier updated");
      setEditTier(null);
      fetchTiers();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to update discount tier"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deleteCode) return;
    setSubmitting(true);
    try {
      await service.deactivateDiscountTier(deleteCode);
      toast.success("Discount tier deactivated");
      setDeleteCode(null);
      fetchTiers();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to deactivate discount tier"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <TabHeader title="Discount Tiers">
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Tier
        </Button>
      </TabHeader>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : tiers.length === 0 ? (
        <p className="text-sm text-muted-foreground">No discount tiers yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tiers.map((tier) => (
              <TableRow key={tier.code}>
                <TableCell className="font-mono text-sm">{tier.code}</TableCell>
                <TableCell className="font-medium">{tier.name}</TableCell>
                <TableCell>{tier.percentageOff}% off</TableCell>
                <TableCell>
                  <ActiveStatusBadge isActive={tier.isActive} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditOpen(tier)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {tier.isActive && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteCode(tier.code)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Discount Tier</DialogTitle>
            <DialogDescription>
              Add a new pricing tier used by promo codes and store products.
            </DialogDescription>
          </DialogHeader>
          <DiscountTierForm
            form={createForm}
            onSubmit={handleCreate}
            onCancel={() => setCreateOpen(false)}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTier} onOpenChange={(o) => !o && setEditTier(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Discount Tier</DialogTitle>
            <DialogDescription>
              Modify the tier — the code itself cannot be changed.
            </DialogDescription>
          </DialogHeader>
          <DiscountTierForm
            form={editForm}
            onSubmit={handleEdit}
            onCancel={() => setEditTier(null)}
            isEdit
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      <DeactivateDialog
        open={!!deleteCode}
        onOpenChange={(o) => !o && setDeleteCode(null)}
        title="Deactivate Discount Tier"
        description="This will mark the tier as inactive. Existing promo codes using it won't be affected immediately, but new ones won't be able to use it."
        onConfirm={handleDeactivate}
        submitting={submitting}
      />
    </div>
  );
}

export default DiscountTiersTab;
